// import components
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, IconButton, Tooltip } from '@mui/material';

// import hooks
import {
  gridFilteredDescendantCountLookupSelector,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid-pro';

// import utils
import { Icon } from '@sdb/planning-ui';
import {
  getChildrenExpanded,
  isGroupOrLeafNode,
  isNavigationKey
} from './DutyLineSummaryGrid.utils';

// import types
import type { IconButtonProps } from '@mui/material';
import type { DutyLineSummaryGridRenderCellParams } from './DutyLineSummaryGrid.types';

export const CustomGridTreeDataGroupingCell = (
  props: DutyLineSummaryGridRenderCellParams
) => {
  'use memo';

  const { id, field, rowNode, tooltips } = props;

  const apiRef = useGridApiContext();
  const filteredDescendantCountLookup = useGridSelector(
    apiRef,
    gridFilteredDescendantCountLookupSelector
  );

  if (!isGroupOrLeafNode(rowNode)) {
    return null;
  }

  const expanded = getChildrenExpanded(rowNode);

  const filteredDescendantCount =
    filteredDescendantCountLookup[rowNode.id] ?? 0;

  const handleKeyDown: IconButtonProps['onKeyDown'] = (event) => {
    if (event.key === ' ') {
      event.stopPropagation();
    }
    if (isNavigationKey(event.key) && !event.shiftKey) {
      apiRef.current.publishEvent('cellKeyDown', props, event);
    }
  };

  const handleClick: IconButtonProps['onClick'] = (event) => {
    apiRef.current.setRowChildrenExpansion(id, !expanded);
    apiRef.current.setCellFocus(id, field);
    event.stopPropagation();
  };

  const tooltip = tooltips.find(
    ({ groupingKey }) => groupingKey === rowNode.groupingKey
  );

  return (
    <Box sx={{ ml: rowNode.depth * 2 }}>
      <Box display="flex" alignItems="center" gap={2}>
        <IconButton
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          tabIndex={-1}
          size="small"
          sx={{
            visibility: filteredDescendantCount > 0 ? 'visible' : 'hidden'
          }}
        >
          {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        </IconButton>
        <Box display="flex" alignItems="center" gap={1}>
          {rowNode.groupingKey}
          {tooltip && (
            <Tooltip title={tooltip.title}>
              <Icon type="circle-info" fontSize="inherit" />
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};
